/**
 * Stella Polaris — language preference helpers.
 *
 * Behaviour:
 *  1. On the *first* visit to /, /es, /insights, or /es/insights, detect the
 *     visitor's browser language. If it doesn't match the URL, redirect them
 *     to the matching translation and remember that we did an auto-redirect
 *     so the toast can offer them a one-tap switch back.
 *  2. Whenever the visitor manually switches via the EN/ES pill (or dismisses
 *     the toast), we persist their choice and never auto-redirect them again.
 *
 * Stored in localStorage:
 *   - sp_lang_pref     : "en" | "es" — manual choice, sticky.
 *   - sp_auto_redir    : "en" | "es" — language we auto-redirected FROM
 *                        (read once, then cleared by the toast component).
 */

const PREF_KEY = "sp_lang_pref";
const AUTO_KEY = "sp_auto_redir";
const SUPPORTED = ["en", "es"];

const safeGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key, val) => {
  try {
    window.localStorage.setItem(key, val);
  } catch {
    /* ignore */
  }
};
const safeDel = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

export const getStoredPref = () => {
  const v = safeGet(PREF_KEY);
  return SUPPORTED.includes(v) ? v : null;
};

export const setStoredPref = (lang) => {
  if (SUPPORTED.includes(lang)) safeSet(PREF_KEY, lang);
};

export const consumeAutoRedirect = () => {
  const v = safeGet(AUTO_KEY);
  if (v) safeDel(AUTO_KEY);
  return SUPPORTED.includes(v) ? v : null;
};

// Module-level cache: in dev, React.StrictMode mounts the toast component
// twice. Without this, the second mount would read `null` (the first mount
// already deleted the localStorage key) and the toast would never render.
let _autoRedirectFlash = null;
export const peekAndCacheAutoRedirect = () => {
  if (_autoRedirectFlash !== null) return _autoRedirectFlash || null;
  const v = consumeAutoRedirect();
  _autoRedirectFlash = v ?? "";
  return v;
};
export const clearAutoRedirectCache = () => {
  _autoRedirectFlash = "";
};

export const detectBrowserLang = () => {
  if (typeof navigator === "undefined") return null;
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  for (const raw of list) {
    if (!raw) continue;
    const code = raw.toLowerCase().split("-")[0];
    if (SUPPORTED.includes(code)) return code;
  }
  return null;
};

/**
 * Decide whether to auto-redirect.
 * Returns the target lang ("en" | "es") if a redirect should happen, else null.
 * Side-effect: writes sp_auto_redir so the toast can read it on the next page.
 */
export const decideAutoRedirect = (currentLang) => {
  if (getStoredPref()) return null; // user has chosen — never override
  const browser = detectBrowserLang();
  if (!browser) return null;
  if (browser === currentLang) return null;
  safeSet(AUTO_KEY, currentLang); // remember the lang we're leaving
  return browser;
};
